import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Navigation as WazeIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  CameraAlt as PhotoIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 * Componente para gestionar acciones (botones) de un estado con drag & drop
 *
 * Props:
 * - accionesAsignadas: Array de acciones ya configuradas para el estado
 * - onAccionesChange: Callback cuando cambian las acciones
 * - readOnly: Si es true, solo lectura
 */
const SelectorAcciones = ({
  accionesAsignadas = [],
  onAccionesChange,
  readOnly = false
}) => {
  const [acciones, setAcciones] = useState(accionesAsignadas);
  const [dialogAgregarOpen, setDialogAgregarOpen] = useState(false);
  const [tipoAccionSeleccionada, setTipoAccionSeleccionada] = useState('');

  // Tipos de acciones disponibles
  const TIPOS_ACCIONES = [
    {
      tipo: 'waze_origen',
      label: 'Waze al Origen',
      descripcion: 'Abre Waze con navegación al punto de origen',
      icon: <WazeIcon />,
      color: '#33CCFF'
    },
    {
      tipo: 'waze_destino',
      label: 'Waze al Destino',
      descripcion: 'Abre Waze con navegación al destino',
      icon: <WazeIcon />,
      color: '#33CCFF'
    },
    {
      tipo: 'llamar',
      label: 'Llamar',
      descripcion: 'Realiza llamada telefónica al cliente',
      icon: <PhoneIcon />,
      color: '#1976d2'
    },
    {
      tipo: 'whatsapp',
      label: 'WhatsApp',
      descripcion: 'Abre chat de WhatsApp con el cliente',
      icon: <WhatsAppIcon />,
      color: '#25D366'
    },
    {
      tipo: 'foto',
      label: 'Foto',
      descripcion: 'Permite tomar foto o seleccionar de galería',
      icon: <PhotoIcon />,
      color: '#ff9800'
    }
  ];

  useEffect(() => {
    setAcciones(accionesAsignadas);
  }, [accionesAsignadas]);

  // Obtener acciones disponibles (no asignadas)
  const accionesDisponibles = TIPOS_ACCIONES.filter(
    ta => !acciones.some(a => a.tipo_accion === ta.tipo)
  );

  // Manejar reordenamiento con drag & drop
  const handleDragEnd = (result) => {
    if (!result.destination || readOnly) return;

    const items = Array.from(acciones);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Actualizar orden
    const accionesActualizadas = items.map((accion, index) => ({
      ...accion,
      orden: index + 1
    }));

    setAcciones(accionesActualizadas);
    if (onAccionesChange) {
      onAccionesChange(accionesActualizadas);
    }
  };

  // Eliminar acción
  const handleEliminarAccion = (tipo_accion) => {
    if (readOnly) return;

    const accionesActualizadas = acciones
      .filter(a => a.tipo_accion !== tipo_accion)
      .map((accion, index) => ({
        ...accion,
        orden: index + 1
      }));

    setAcciones(accionesActualizadas);
    if (onAccionesChange) {
      onAccionesChange(accionesActualizadas);
    }
  };

  // Agregar acción
  const handleAgregarAccion = () => {
    if (readOnly || !tipoAccionSeleccionada) return;

    const tipoInfo = TIPOS_ACCIONES.find(ta => ta.tipo === tipoAccionSeleccionada);
    if (!tipoInfo) return;

    const nuevaAccion = {
      tipo_accion: tipoAccionSeleccionada,
      orden: acciones.length + 1,
      label: tipoInfo.label,
      descripcion: tipoInfo.descripcion
    };

    const accionesActualizadas = [...acciones, nuevaAccion];
    setAcciones(accionesActualizadas);
    if (onAccionesChange) {
      onAccionesChange(accionesActualizadas);
    }

    setTipoAccionSeleccionada('');
    setDialogAgregarOpen(false);
  };

  const getIconForTipo = (tipo) => {
    const tipoInfo = TIPOS_ACCIONES.find(ta => ta.tipo === tipo);
    return tipoInfo ? tipoInfo.icon : null;
  };

  const getColorForTipo = (tipo) => {
    const tipoInfo = TIPOS_ACCIONES.find(ta => ta.tipo === tipo);
    return tipoInfo ? tipoInfo.color : '#666';
  };

  const getLabelForTipo = (tipo) => {
    const tipoInfo = TIPOS_ACCIONES.find(ta => ta.tipo === tipo);
    return tipoInfo ? tipoInfo.label : tipo;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Acciones del Estado ({acciones.length})
        </Typography>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogAgregarOpen(true)}
            disabled={accionesDisponibles.length === 0}
          >
            Agregar Acción
          </Button>
        )}
      </Box>

      {/* Info */}
      {!readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Las acciones son los botones que aparecen en la app móvil. Arrastra para reordenar.
        </Alert>
      )}

      {/* Lista de acciones con drag & drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="acciones-list">
          {(provided, snapshot) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'background.paper',
                borderRadius: 1,
                p: 1,
                minHeight: acciones.length === 0 ? 100 : 'auto'
              }}
            >
              {acciones.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No hay acciones configuradas para este estado.
                      {!readOnly && ' Haz clic en "Agregar Acción" para comenzar.'}
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                acciones.map((accion, index) => (
                  <Draggable
                    key={accion.tipo_accion}
                    draggableId={accion.tipo_accion}
                    index={index}
                    isDragDisabled={readOnly}
                  >
                    {(provided, snapshot) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 1,
                          bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
                          boxShadow: snapshot.isDragging ? 4 : 1,
                          transition: 'all 0.2s'
                        }}
                      >
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Grid container alignItems="center" spacing={2}>
                            {/* Drag Handle */}
                            <Grid item>
                              <div {...provided.dragHandleProps}>
                                <DragIcon
                                  sx={{
                                    color: readOnly ? 'action.disabled' : 'action.active',
                                    cursor: readOnly ? 'default' : 'grab'
                                  }}
                                />
                              </div>
                            </Grid>

                            {/* Orden */}
                            <Grid item>
                              <Chip
                                label={index + 1}
                                size="small"
                                sx={{ width: 32 }}
                              />
                            </Grid>

                            {/* Icono de la acción */}
                            <Grid item>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  bgcolor: getColorForTipo(accion.tipo_accion),
                                  color: 'white'
                                }}
                              >
                                {getIconForTipo(accion.tipo_accion)}
                              </Box>
                            </Grid>

                            {/* Información de la acción */}
                            <Grid item xs>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {getLabelForTipo(accion.tipo_accion)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {accion.descripcion ||
                                   TIPOS_ACCIONES.find(ta => ta.tipo === accion.tipo_accion)?.descripcion}
                                </Typography>
                              </Box>
                            </Grid>

                            {/* Tipo técnico */}
                            <Grid item>
                              <Chip
                                label={accion.tipo_accion}
                                size="small"
                                variant="outlined"
                              />
                            </Grid>

                            {/* Botón eliminar */}
                            {!readOnly && (
                              <Grid item>
                                <Tooltip title="Eliminar acción">
                                  <IconButton
                                    onClick={() => handleEliminarAccion(accion.tipo_accion)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>

      {/* Dialog para agregar acción */}
      <Dialog
        open={dialogAgregarOpen}
        onClose={() => {
          setDialogAgregarOpen(false);
          setTipoAccionSeleccionada('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar Acción</DialogTitle>
        <DialogContent>
          {accionesDisponibles.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Ya has agregado todas las acciones disponibles.
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Acción</InputLabel>
                <Select
                  value={tipoAccionSeleccionada}
                  onChange={(e) => setTipoAccionSeleccionada(e.target.value)}
                  label="Tipo de Acción"
                >
                  {accionesDisponibles.map((tipo) => (
                    <MenuItem key={tipo.tipo} value={tipo.tipo}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: tipo.color,
                            color: 'white'
                          }}
                        >
                          {tipo.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1">{tipo.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tipo.descripcion}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogAgregarOpen(false);
            setTipoAccionSeleccionada('');
          }}>
            Cancelar
          </Button>
          {accionesDisponibles.length > 0 && (
            <Button
              variant="contained"
              onClick={handleAgregarAccion}
              disabled={!tipoAccionSeleccionada}
            >
              Agregar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelectorAcciones;
